// 'both' widens the view (and bulk operations / the wave deploy loop) to
// Cloud Run and Terraform components together, so one deploy group can ship
// both kinds in a single run.
export type Category = 'cloudrun' | 'terraform' | 'both';
export type ViewPattern = 'split' | 'combined';

export interface GroupFilter {
  category: Category;
  /**
   * Include maintenance-mode components (shown by default; unticking hides
   * them to narrow the view to actively developed components). Session-only:
   * the owner holds it in plain state and never persists it, so missing
   * (e.g. state persisted by older versions) means included.
   */
  includeMaintenance?: boolean;
}

export const EMPTY_FILTER: GroupFilter = {
  category: 'cloudrun',
};

/**
 * Clamp a persisted category to a valid value: older versions stored
 * 'application' (renamed to 'cloudrun'), and anything else unknown falls back
 * to the default.
 */
export function normalizeCategory(category: unknown): Category {
  return category === 'terraform' || category === 'both'
    ? category
    : 'cloudrun';
}

/** Terraform projects follow the `<name>-tf` naming convention. */
export function isTerraform(projectName: string): boolean {
  return projectName.endsWith('-tf');
}

/** Component-level category filter (Terraform vs Cloud Run; 'both' keeps all). */
export function matchesCategory(
  projectName: string,
  category: Category,
): boolean {
  if (category === 'both') return true;
  return category === 'terraform'
    ? isTerraform(projectName)
    : !isTerraform(projectName);
}

// Deployment groups organize a team's components into independently operated
// deploy sets; a component may belong to several. Components without the
// deploy-groups annotation belong to no group and are reachable only via
// "all"; a team with no annotated component sees no group selector (the UI
// stays exactly as before the concept existed).

// Pseudo-group selecting every component regardless of its deployment groups.
// It is the selector's default so a team sees its whole fleet unless it
// deliberately narrows to one group. Never part of distinctDeployGroups —
// the UI prepends it as a selector option.
export const ALL_DEPLOY_GROUPS = 'all';

/** True when the component belongs to the selected group ("all" matches everything). */
export function matchesDeployGroup(
  groups: string[] | undefined,
  selected: string,
): boolean {
  return (
    selected === ALL_DEPLOY_GROUPS ||
    normalizeDeployGroups(groups).includes(selected)
  );
}

/** Group list normalized against missing/blank values (may be empty). */
export function normalizeDeployGroups(groups: string[] | undefined): string[] {
  return [...new Set((groups ?? []).map(g => g.trim()).filter(Boolean))];
}

/** Distinct deployment group names, alphabetical. */
export function distinctDeployGroups(
  groupLists: Array<string[] | undefined>,
): string[] {
  const names = new Set(groupLists.flatMap(normalizeDeployGroups));
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Maintenance-mode components are hidden only when explicitly excluded. */
export function matchesMaintenance(
  maintenanceMode: boolean | undefined,
  includeMaintenance: boolean | undefined,
): boolean {
  return !maintenanceMode || includeMaintenance !== false;
}
